import { Link } from 'react-router-dom'
import { BookOpen, Layers, LayoutGrid, ShieldCheck, Users, Wallet } from 'lucide-react'
import { PublicFooter, PublicHeader } from '../components/PublicLayout'

// ASYS for accountants: what a practice gets, how clients come in, and the
// questions accountants ask, with one way in (a free practice).

const STEPS = [
  ['Create your practice', 'Free, in a minute. You get a Company ID that you and your staff sign in with.'],
  ['Bring in your clients', 'A client already on ASYS invites you from Settings → Accountant. Or add a client yourself: they get a 30-day free trial, then pay for their own plan.'],
  ['Open their books', 'Journal entries, vendor invoices, the bank, the month-end close and every report, as the company’s own accountant.'],
]

const FEATURES = [
  [LayoutGrid, 'Every client on one page', 'Cash, what’s owed both ways and this month’s profit on each client, and what’s waiting: drafts, vendor invoices, bank lines, the month to close.'],
  [ShieldCheck, 'The client chooses', 'Full books or View only. They can change it or remove you at any time, and their audit log shows what you did.'],
  [Users, 'Your whole firm', 'Add your staff. Admins open every client; staff open the clients you give them.'],
  [BookOpen, 'Real accounting', 'A classified balance sheet, a multi-step income statement, the general ledger, bank reconciliation and the month-end close.'],
  [Layers, 'Any plan', 'You see a client’s books even on a Basic plan, where they don’t see Accounting themselves: their sales and bills post to the books either way.'],
  [Wallet, 'Free for you', 'Your practice costs nothing, and your people don’t count toward a client’s users. Each client pays for its own plan.'],
]

const FAQ = [
  ['Is it really free for my practice?', 'Yes. A practice has no plan to pay for. Each client pays for its own plan, as any company on ASYS does, and your practice’s people don’t count toward its users.'],
  ['How do clients add me?', 'In their ASYS, under Settings → Accountant, they enter your email and choose Full books or View only. You get an email with a link: accept it with your practice, or create one from the same page.'],
  ['Can I bring a client who isn’t on ASYS yet?', 'Yes. Add a client from your Clients list. The company is set up with a 30-day free trial of the plan you choose, and its owner gets an email to choose a password. Your practice gets Full books; the owner can change that.'],
  ['What can I do with View only?', 'Read everything: the ledgers, the statements and the month-end pack. Saving or posting anything is refused.'],
  ['What happens if a client removes me?', 'You lose access at once: your next click in their books takes you back to your Clients list. Nothing in their books changes.'],
  ['Does it work on the phone?', 'Yes. Sign in to the ASYS Retail app with your practice’s Company ID; your clients are under Clients.'],
]

/** A small, made-up Clients list, to show what a practice sees. */
function ClientsPreview() {
  const rows = [
    ['TF', 'Town Furniture', '124,500.00', [['2 drafts', '']]],
    ['CM', 'Corner Market', '48,250.00', [['September not closed', 'warn']]],
    ['VB', 'Village Bakery', '6,120.00', [['Up to date', 'up']]],
  ]
  return (
    <div className="lp-preview fa-preview" aria-hidden="true">
      <div className="lp-preview-title">
        <span className="pub-brand-mark fa-mark">MS</span>
        <div><strong>Main Street Accounting</strong><span>Clients</span></div>
      </div>
      {rows.map(([mark, name, cash, chips]) => (
        <div key={name} className="fa-client">
          <span className="fa-client-mark">{mark}</span>
          <div className="fa-client-main">
            <strong>{name}</strong>
            <span>Cash {cash}</span>
          </div>
          <div className="fa-client-chips">{chips.map(([c, tone]) => <em key={c} className={tone}>{c}</em>)}</div>
        </div>
      ))}
    </div>
  )
}

export default function ForAccountants() {
  return (
    <div className="pub">
      <PublicHeader>
        <Link className="pub-btn pub-btn-text fa-hide-sm" to="/">For businesses</Link>
        <Link className="pub-btn pub-btn-text" to="/login">Sign in</Link>
        <Link className="pub-btn pub-btn-dark" to="/accountants/signup"><span className="fa-hide-sm">Create a free practice</span><span className="fa-show-sm">Start free</span></Link>
      </PublicHeader>

      <main>
        <section className="lp-hero pub-wrap">
          <div className="lp-hero-text">
            <h1>Your clients’ books, <span>all in one place.</span></h1>
            <p>ASYS for accountants and bookkeepers. Your clients run their business on ASYS and pay for their own plan; you open their books from one Clients list.</p>
            <div className="lp-actions">
              <Link className="pub-btn pub-btn-primary pub-btn-lg" to="/accountants/signup">Create a free practice</Link>
              <Link className="pub-btn pub-btn-ghost pub-btn-lg" to="/login">Sign in</Link>
            </div>
            <p className="lp-small">Free for your practice · Any client plan, Basic included · You and your staff</p>
          </div>
          <ClientsPreview />
        </section>

        <section className="pub-wrap lp-section" aria-labelledby="fa-how">
          <h2 id="fa-how">How it works</h2>
          <ol className="fa-steps">
            {STEPS.map(([title, text], i) => (
              <li key={title}><span className="fa-step-n">{i + 1}</span><strong>{title}</strong><p>{text}</p></li>
            ))}
          </ol>
        </section>

        <section className="pub-wrap lp-section" aria-labelledby="fa-what">
          <h2 id="fa-what">Made for the way you work</h2>
          <div className="fa-grid">
            {FEATURES.map(([Icon, title, text]) => (
              <div key={title} className="fa-card">
                <span className="lp-icon"><Icon size={18} /></span>
                <strong>{title}</strong>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pub-wrap lp-section" aria-labelledby="fa-questions">
          <h2 id="fa-questions">Questions</h2>
          <div className="pr-faq">
            {FAQ.map(([q, a]) => (
              <details key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="pub-wrap lp-section">
          <div className="lp-band">
            <h2>Bring your first client today.</h2>
            <p>Your practice is free, for as many clients as you look after.</p>
            <div className="lp-actions lp-actions-center">
              <Link className="pub-btn pub-btn-white pub-btn-lg" to="/accountants/signup">Create a free practice</Link>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter>
        <Link to="/">For businesses</Link>
        <Link to="/login">Sign in</Link>
      </PublicFooter>
    </div>
  )
}
